import { Switch } from "@/components/shadcn/switch";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Edit, Trash } from "lucide-react";
import { AddNewEntry, Tool } from "./AssistantDetailsContent";
import { useMemo, useState } from "react";
import { NoxusMenu } from "../UI/NoxusMenu";
import { ActionToolSettingsInput, NodeDefinition } from "@/generated/types.gen";
import { ActionPickerModal } from "./ActionPickerModal";
import { useQuery } from "@tanstack/react-query";
import { InternalService } from "@/generated";
import { useDisclosure } from "@/utils/useDisclosure";
import { cn } from "@/lib/utils";
import { UnavailableResourceBadge } from "./UnavailableResourceBadge";

export const ActionsField = ({
  tools,
  onToolsChange,
  isNodeConfig,
  isDisabled,
}: {
  tools: Tool[] | undefined;
  onToolsChange: (tools: Tool[]) => void;
  isNodeConfig?: boolean;
  isDisabled?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const actionsTools = useMemo(() => {
    return tools?.filter((tool) => tool.type === "action");
  }, [tools]) as ActionToolSettingsInput[] | undefined;

  const onChange = (tool?: Tool, enabled?: boolean) => {
    if (isDisabled) return;
    if (!tool) {
      return;
    }
    const newTools = tools?.map((t) => {
      if (t === tool) {
        return { ...t, enabled };
      }
      return t;
    });

    if (newTools) {
      onToolsChange(newTools);
    }
  };

  const onAddAction = (props: {
    node_name: string;
    value: string;
    tool_config: import("@/generated").ActionToolConfig | null;
  }) => {
    const { node_name, value, tool_config } = props;
    if (isDisabled) return;
    const extraInstructions = value ? { text: value, definition: {} } : null;
    if (editingTool) {
      onToolsChange(
        (tools?.map((t) => {
          if (t === editingTool) {
            return {
              ...t,
              extra_instructions: extraInstructions,
              tool_config,
            };
          }
          return t;
        }) || []) as Tool[]
      );
    } else {
      onToolsChange([
        ...(tools || []),
        {
          type: "action",
          enabled: true,
          node_name,
          extra_instructions: extraInstructions,
          tool_config,
        } as ActionToolSettingsInput,
      ]);
    }
  };

  const activeActions = useMemo(() => {
    return (
      actionsTools?.map((tool) => ({
        id: tool.node_name,
        name: tool.node_name,
      })) || []
    );
  }, [actionsTools]);

  const [editingTool, setEditingTool] = useState<
    ActionToolSettingsInput | undefined
  >(undefined);

  const onEdit = (tool: ActionToolSettingsInput) => {
    if (isDisabled) return;
    setEditingTool(tool);
    onOpen();
  };

  const { data: nodesData, isLoading: isLoadingNodes } = useQuery({
    queryKey: ["nodes", "tools"],
    queryFn: () => InternalService.getNodes({ query: { is_tool: true } }),
    staleTime: Infinity,
  });

  const nodeByType = useMemo(() => {
    const map = new Map(
      (nodesData?.data ?? []).map((node) => [node.type, node])
    );
    return map;
  }, [nodesData]);

  const onDelete = (tool: ActionToolSettingsInput) => {
    if (isDisabled) return;
    if (tools) {
      onToolsChange(tools.filter((t) => t !== tool));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-foreground leading-none">
            Actions
          </p>
          <p className="text-xs font-regular text-muted-foreground mt-1.5">
            Select flow actions this agent can execute.
          </p>
        </div>

        {actionsTools?.map((tool, index) => (
          <ActionEntry
            key={`${tool.node_name}-${index}`}
            tool={tool}
            node={nodeByType.get(tool.node_name)}
            isLoading={isLoadingNodes}
            onChange={(enabled) => {
              onChange(tool, enabled);
            }}
            onDelete={() => {
              onDelete(tool);
            }}
            onEdit={() => {
              onEdit(tool);
            }}
            isDisabled={isDisabled}
          />
        ))}

        <AddNewEntry
          label="Add Action"
          onClick={onOpen}
          isDisabled={isDisabled}
        />
      </div>
      {isOpen && (
        <ActionPickerModal
          isOpen={isOpen}
          onClose={() => {
            setEditingTool(undefined);
            onClose();
          }}
          onSelectAction={onAddAction}
          openActions={activeActions}
          editingTool={editingTool}
        />
      )}
    </div>
  );
};

const ActionEntry = ({
  tool,
  node,
  isLoading,
  onChange,
  onDelete,
  onEdit,
  isDisabled,
}: {
  tool: ActionToolSettingsInput;
  node?: NodeDefinition;
  isLoading: boolean;
  onChange: (enabled: boolean) => void;
  onDelete: () => void;
  onEdit: () => void;
  isDisabled?: boolean;
}) => {
  const { title, image } = node || {};
  const isMissing = !isLoading && !node;
  const displayName = title || tool.title || tool.node_name;

  return (
    <div className="flex items-center justify-between gap-2 p-2 border border-border rounded-lg w-full">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {image && (
          <img src={image} alt={displayName} className="w-6 h-6 rounded" />
        )}
        <div className="flex-1 flex items-center gap-2 min-w-0">
          {isLoading ? (
            <Skeleton className="h-5 w-32 mb-1" />
          ) : (
            <>
              <p
                className={cn(
                  "font-sm text-sm truncate",
                  isMissing && "text-muted-foreground"
                )}
              >
                {displayName}
              </p>
              {isMissing && <UnavailableResourceBadge kind="action" />}
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={tool.enabled}
          onCheckedChange={(checked) => onChange(checked)}
          disabled={isDisabled || isMissing}
        />
        <NoxusMenu
          itemsArray={[
            {
              label: "Edit",
              icon: Edit,
              onClick: onEdit,
              isHidden: isMissing,
            },
            {
              label: "Delete",
              icon: Trash,
              onClick: onDelete,
              color: "destructive",
            },
          ]}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};
